/**
 * AssignSeatsModal - Modal shown after checkout to help owners assign newly purchased seats
 * Pre-selects team members who have pending upgrade requests
 */

import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from '../ui/dialog';
import { Button } from '../ui/button';
import { 
  Users, 
  Check, 
  Loader2, 
  UserPlus,
  Crown,
  AlertCircle,
  ArrowRight,
  Sparkles
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useSafeMode } from '../../contexts/SafeModeContext';
import { billingApi } from '../../services/api';
import api from '../../services/api';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const AssignSeatsModal = ({
  isOpen,
  onClose,
  newSeats = 0,  // Number of new seats purchased
  onComplete,
}) => {
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  const navigate = useNavigate();
  
  const [isLoading, setIsLoading] = useState(true);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [isAssigning, setIsAssigning] = useState(false);
  const [seatInfo, setSeatInfo] = useState({ available: 0, used: 0, total: 0 });
  
  const fetchData = async () => {
    setIsLoading(true);
    try {
      // Fetch pending upgrade requests
      const requestsResponse = await billingApi.getPendingUpgradeRequests();
      const requests = requestsResponse.data?.requests || [];
      setPendingRequests(requests);
      
      // Pre-select requesters
      const requesterIds = requests.map(r => r.requester_id);
      setSelectedMembers(requesterIds);
      
      // Fetch team members to show who can be assigned seats
      const teamResponse = await api.get('/api/team');
      const members = teamResponse.data?.members || [];
      
      // Filter to show members who don't have paid roles yet (collaborators)
      // or pending requesters
      const eligibleMembers = members.filter(m => 
        m.role === 'collaborator' || requesterIds.includes(m.user_id)
      );
      setTeamMembers(eligibleMembers);
      
      // Get seat info - use new field names with legacy fallbacks
      const contextResponse = await billingApi.getUpgradeContext('seats');
      const context = contextResponse.data;
      setSeatInfo({
        available: context?.seats_available || newSeats,
        used: context?.seats_occupied || 0,
        total: context?.seats_total || newSeats,
        purchased: context?.seats_purchased || newSeats,
        comped: context?.seats_comped || 0,
        planLimit: context?.plan_seat_limit || null
      });
      
    } catch (error) {
      console.error('Failed to fetch data for seat assignment:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Fetch pending requests and team members
  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen, newSeats]);
  
  const toggleMember = (memberId) => {
    setSelectedMembers(prev => {
      if (prev.includes(memberId)) {
        return prev.filter(id => id !== memberId);
      } else if (prev.length < seatInfo.available) {
        return [...prev, memberId];
      } else {
        toast.error(`You can only assign ${seatInfo.available} seat(s)`);
        return prev;
      }
    });
  };
  
  const handleAssignSeats = async () => {
    if (selectedMembers.length === 0) {
      toast.info('No members selected');
      return;
    }
    
    setIsAssigning(true);
    try {
      // Assign seats by upgrading roles to product_manager
      const results = await Promise.all(
        selectedMembers.map(async (userId) => {
          try {
            await api.patch(`/api/team/members/${userId}`, {
              role: 'product_manager'
            });
            return { userId, success: true };
          } catch (error) {
            return { userId, success: false, error };
          }
        })
      );
      
      const successCount = results.filter(r => r.success).length;
      
      if (successCount > 0) {
        toast.success(`${successCount} seat(s) assigned! 🎉`, {
          description: 'Team members have been upgraded to Product Manager role.',
        });
        
        // Mark upgrade requests as resolved
        await billingApi.dismissAllUpgradeRequests();
      }
      
      onComplete?.();
      onClose();
      
    } catch (error) {
      console.error('Failed to assign seats:', error);
      toast.error('Failed to assign seats');
    } finally {
      setIsAssigning(false);
    }
  };
  
  const handleGoToTeam = () => {
    onClose();
    navigate('/team');
  };
  
  const handleSkip = () => {
    toast.info('You can assign seats anytime from the Team page');
    onClose();
  };
  
  // Get member info from pending request
  const getMemberInfo = (request) => {
    const member = teamMembers.find(m => m.user_id === request.requester_id);
    return member || {
      user_id: request.requester_id,
      name: request.requester_name,
      email: request.requester_email,
      role: 'collaborator'
    };
  };
  
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className={cn("max-w-lg", colors.bgCard, colors.borderPrimary)}>
        <DialogHeader>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <DialogTitle className={cn("text-xl", colors.textPrimary)}>
                Upgrade Complete! 🎉
              </DialogTitle>
              <DialogDescription className={cn("text-sm mt-1", colors.textSecondary)}>
                You now have {seatInfo.available} seat{seatInfo.available !== 1 ? 's' : ''} available to assign.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-green-500" />
          </div>
        ) : (
          <div className="py-4 space-y-4">
            {/* Seat Status */}
            <div className={cn("p-4 rounded-lg border", colors.bgTertiary, "border-green-200 dark:border-green-800")}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-green-600" />
                  <span className={cn("font-medium", colors.textPrimary)}>Available Seats</span>
                </div>
                <span className="text-2xl font-bold text-green-600">
                  {seatInfo.available}
                </span>
              </div>
              {seatInfo.planLimit && (
                <div className="mt-2 pt-2 border-t border-green-200 dark:border-green-700 text-xs text-gray-600 dark:text-gray-400">
                  <div className="flex justify-between">
                    <span>Total seats:</span>
                    <span>{seatInfo.total} / {seatInfo.planLimit} max</span>
                  </div>
                  {seatInfo.comped > 0 && (
                    <div className="flex justify-between mt-1">
                      <span>Includes comped:</span>
                      <span className="text-green-600">{seatInfo.comped} free</span>
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Pending Requests Section */}
            {pendingRequests.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-500" />
                  <span className={cn("text-sm font-medium", colors.textSecondary)}>
                    Team members waiting for seats
                  </span>
                </div>
                
                <div className="space-y-2">
                  {pendingRequests.map((request) => {
                    const member = getMemberInfo(request);
                    const isSelected = selectedMembers.includes(request.requester_id);
                    
                    return (
                      <button
                        key={request.requester_id}
                        onClick={() => toggleMember(request.requester_id)}
                        className={cn(
                          "w-full p-3 rounded-lg border text-left transition-all",
                          isSelected
                            ? "bg-green-50 dark:bg-green-900/20 border-green-400 dark:border-green-600"
                            : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-green-300"
                        )}
                      >
                        <div className="flex items-center gap-3">
                          <div className={cn(
                            "w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium",
                            isSelected
                              ? "bg-green-500 text-white"
                              : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
                          )}>
                            {isSelected ? (
                              <Check className="w-5 h-5" />
                            ) : (
                              (member.name || member.email || '?')[0].toUpperCase()
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={cn("font-medium truncate", colors.textPrimary)}>
                              {member.name || member.email}
                            </p>
                            <p className={cn("text-xs truncate", colors.textMuted)}>
                              {member.email} • Requested upgrade
                            </p>
                            {request.message && (
                              <p className={cn("text-xs mt-1 italic", colors.textMuted)}>
                                &ldquo;{request.message}&rdquo;
                              </p>
                            )}
                          </div>
                          <Crown className={cn(
                            "w-5 h-5 shrink-0",
                            isSelected ? "text-green-500" : "text-gray-300 dark:text-gray-600"
                          )} />
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Other eligible members */}
            {teamMembers.filter(m => !pendingRequests.find(r => r.requester_id === m.user_id)).length > 0 && (
              <div className="space-y-2">
                <span className={cn("text-sm font-medium", colors.textSecondary)}>
                  Other team members (Collaborators)
                </span>
                
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {teamMembers
                    .filter(m => !pendingRequests.find(r => r.requester_id === m.user_id))
                    .map((member) => {
                      const isSelected = selectedMembers.includes(member.user_id);
                      
                      return (
                        <button
                          key={member.user_id}
                          onClick={() => toggleMember(member.user_id)}
                          className={cn(
                            "w-full p-3 rounded-lg border text-left transition-all",
                            isSelected
                              ? "bg-green-50 dark:bg-green-900/20 border-green-400 dark:border-green-600"
                              : "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-green-300"
                          )}
                        >
                          <div className="flex items-center gap-3">
                            <div className={cn(
                              "w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium",
                              isSelected
                                ? "bg-green-500 text-white"
                                : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
                            )}>
                              {isSelected ? (
                                <Check className="w-4 h-4" />
                              ) : (
                                (member.name || member.email || '?')[0].toUpperCase()
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={cn("text-sm font-medium truncate", colors.textPrimary)}>
                                {member.name || member.email}
                              </p>
                              <p className={cn("text-xs truncate", colors.textMuted)}>
                                {member.role}
                              </p>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                </div>
              </div>
            )}
            
            {/* No pending requests */}
            {pendingRequests.length === 0 && teamMembers.length === 0 && (
              <div className={cn("p-4 rounded-lg text-center", colors.bgTertiary)}>
                <UserPlus className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                <p className={colors.textSecondary}>
                  No team members waiting for seats.
                </p>
                <p className={cn("text-sm mt-1", colors.textMuted)}>
                  You can invite new members or upgrade existing collaborators from the Team page.
                </p>
              </div>
            )}
            
            {/* Selection count */}
            {selectedMembers.length > 0 && (
              <div className={cn("text-sm text-center", colors.textMuted)}>
                {selectedMembers.length} of {seatInfo.available} seat{seatInfo.available !== 1 ? 's' : ''} selected
              </div>
            )}
          </div>
        )}
        
        <DialogFooter className="flex gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button 
            variant="outline" 
            onClick={handleSkip}
            className={cn("border-gray-300 dark:border-gray-600", colors.textPrimary)}
          >
            Skip for now
          </Button>
          
          <Button 
            variant="outline" 
            onClick={handleGoToTeam}
            className="border-blue-300 text-blue-600 hover:bg-blue-50 dark:border-blue-700 dark:text-blue-400"
          >
            <Users className="w-4 h-4 mr-2" />
            Go to Team
          </Button>
          
          {selectedMembers.length > 0 && (
            <Button
              onClick={handleAssignSeats}
              disabled={isAssigning}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {isAssigning ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Check className="w-4 h-4 mr-2" />
              )}
              Assign {selectedMembers.length} Seat{selectedMembers.length !== 1 ? 's' : ''}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AssignSeatsModal;
